import React, { useState, useEffect } from 'react';
import { analyticsService } from '../../services/analyticsService';
import { useLanguage } from '../../contexts/LanguageContext';
import { DollarSign, TrendingUp, TrendingDown, PieChart, BarChart3, AlertTriangle, Zap, Target } from 'lucide-react';

const AICosts = () => {
    const [costData, setCostData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState('today');
    const { t } = useLanguage();

    useEffect(() => {
        loadCostData();
    }, [period]);

    const loadCostData = async () => {
        try {
            setLoading(true);
            const response = await analyticsService.getAICostAnalysis(period);
            if (response.success) {
                setCostData(response.data);
            }
        } catch (error) {
            console.error(t('loadStatsError'), error);
        } finally {
            setLoading(false);
        }
    };

    // Token maliyetini hesapla (örnek hesaplama - gerçek değerlerle değiştirilebilir)
    const calculateCosts = (tokens) => {
        const costPer1KTokens = 0.002; // Örnek maliyet: $0.002 per 1K tokens
        return (tokens / 1000) * costPer1KTokens;
    };

    // Verimlilik skoru hesapla
    const calculateEfficiency = (avgTokens, totalMessages) => {
        const baseEfficiency = 100 - (avgTokens / 100); // Basit bir formül
        return Math.max(65, Math.min(95, baseEfficiency)); // 65-95 arasında sınırla
    };

    if (loading) {
        return (
            <div className="loading-container">
                <div className="loading-spinner"></div>
                <div>{t('loading')}</div>
            </div>
        );
    }

    if (!costData) {
        return <div className="no-data">{t('noStatsAvailable')}</div>;
    }

    const generalStats = costData.generalStatistics;
    const planStats = costData.planStatistics;
    const totalTokens = generalStats?.total_tokens || 0;
    const totalCost = calculateCosts(totalTokens);
    const efficiencyScore = calculateEfficiency(generalStats?.avg_tokens_per_message, generalStats?.total_messages);

    return (
        <div className="ai-costs">
            {/* Period Selector */}
            <div className="period-selector">
                <div className="period-buttons">
                    {['today', 'week', 'month', 'year'].map(p => (
                        <button
                            key={p}
                            className={`period-button ${period === p ? 'active' : ''}`}
                            onClick={() => setPeriod(p)}
                        >
                            {t(p)}
                        </button>
                    ))}
                </div>
            </div>

            {/* Cost Overview */}
            <div className="cost-overview">
                <div className="cost-card primary">
                    <div className="cost-icon">
                        <DollarSign size={24} />
                    </div>
                    <div className="cost-content">
                        <h3>${totalCost.toFixed(2)}</h3>
                        <p>{t('monthlyCost')}</p>
                        <div className="cost-trend positive">
                            <TrendingDown size={14} />
                            <span>%12 azalmış</span>
                        </div>
                    </div>
                </div>

                <div className="cost-card">
                    <div className="cost-icon">
                        <Target size={24} />
                    </div>
                    <div className="cost-content">
                        <h3>{efficiencyScore}%</h3>
                        <p>{t('efficiencyScore')}</p>
                        <div className="cost-trend positive">
                            <TrendingUp size={14} />
                            <span>%5 artmış</span>
                        </div>
                    </div>
                </div>

                <div className="cost-card">
                    <div className="cost-icon">
                        <PieChart size={24} />
                    </div>
                    <div className="cost-content">
                        <h3>${(totalCost / 30).toFixed(2)}</h3>
                        <p>{t('dailyAvgCost')}</p>
                    </div>
                </div>

                <div className="cost-card">
                    <div className="cost-icon">
                        <BarChart3 size={24} />
                    </div>
                    <div className="cost-content">
                        <h3>${(calculateCosts(1000)).toFixed(4)}</h3>
                        <p>{t('costPerToken')}</p>
                    </div>
                </div>
            </div>

            {/* Cost Analysis */}
            <div className="cost-analysis">
                <div className="analysis-card">
                    <div className="card-header">
                        <h3>{t('costBreakdown')}</h3>
                        <AlertTriangle size={16} />
                    </div>
                    <div className="cost-breakdown">
                        {planStats?.map(plan => {
                            const planCost = calculateCosts(plan.tokens_used);
                            const percentage = (plan.tokens_used / totalTokens) * 100;

                            return (
                                <div key={plan.plan} className="breakdown-item">
                                    <div className="breakdown-info">
                                        <span className={`plan-badge ${plan.plan}`}>{t(plan.plan)}</span>
                                        <span className="breakdown-cost">
                                            ${planCost.toFixed(2)}
                                        </span>
                                    </div>
                                    <div className="breakdown-bar">
                                        <div
                                            className={`bar-fill ${plan.plan}`}
                                            style={{ width: `${percentage}%` }}
                                        ></div>
                                    </div>
                                    <div className="breakdown-stats">
                                        <span>{plan.tokens_used?.toLocaleString()} {t('tokens')}</span>
                                        <span>{plan.message_count} {t('messages')}</span>
                                        <span className="percentage">{percentage.toFixed(1)}%</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="analysis-card">
                    <div className="card-header">
                        <h3>{t('optimizationTips')}</h3>
                        <Zap size={16} />
                    </div>
                    <div className="optimization-tips">
                        <div className="tip-item">
                            <div className="tip-icon">🚀</div>
                            <div className="tip-content">
                                <h4>Token Kullanımını Optimize Et</h4>
                                <p>Gereksiz token kullanımını azaltmak için model ayarlarını gözden geçirin. Daha verimli modeller kullanın.</p>
                            </div>
                        </div>
                        <div className="tip-item">
                            <div className="tip-icon">📊</div>
                            <div className="tip-content">
                                <h4>Kullanım Trendlerini İzle</h4>
                                <p>Yüksek maliyetli kullanıcıları belirleyip optimizasyon önerileri sunun.</p>
                            </div>
                        </div>
                        <div className="tip-item">
                            <div className="tip-icon">💡</div>
                            <div className="tip-content">
                                <h4>Akıllı Önbellekleme</h4>
                                <p>Benzer sorgular için önbellek mekanizması kullanarak maliyetleri %30'a kadar düşürün.</p>
                            </div>
                        </div>
                        <div className="tip-item">
                            <div className="tip-icon">⚡</div>
                            <div className="tip-content">
                                <h4>Model Seçimini Optimize Et</h4>
                                <p>Görev tipine göre en uygun modeli seçerek hem performansı artırın hem de maliyetleri düşürün.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Savings Potential */}
            <div className="savings-section">
                <div className="savings-card">
                    <div className="savings-header">
                        <Zap size={20} />
                        <h3>{t('monthlySavings')} Potansiyeli</h3>
                    </div>
                    <div className="savings-content">
                        <div className="savings-metric">
                            <span className="metric-value">${(totalCost * 0.25).toFixed(2)}</span>
                            <span className="metric-label">Potansiyel Aylık Tasarruf</span>
                        </div>
                        <div className="savings-progress">
                            <div className="progress-bar">
                                <div className="progress-fill" style={{ width: '75%' }}></div>
                            </div>
                            <div className="progress-labels">
                                <span>Mevcut Maliyet</span>
                                <span>Optimize Edilmiş</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AICosts;